import { NextLinkButton } from "@/lib/next-mui/NextLinkButton";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { css } from "@emotion/css";
import { HeroSideBySide } from "../HeroSideBySide";
import { AudienceTabs } from "../AudienceTabs";
import { PricingContent } from "@/modules/billing/pricing/PricingPageContent";

function formatEur(amount: number) {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "EUR",
  }).format(amount);
}

const headline = `Interactive transcripts for your audience`;
const subheadlines = [
  `Make your transcripts interactive on your own website with our embeddable widget.`,
  `Listeners can follow along the transcript with synced highlighting, and can request auto-generated translations for any paragraph.`,
];

// `Can be embedded on any website where embedding custom HTML and JavaScript is allowed.`,
// `Synchronization works with the Acast player component. Let us know if you would like to use a different player.`,

const features = [
  {
    title: "Step 1. Upload your audio",
    detail:
      "Upload your audio and get an accurate, timestamped transcript powered by the latest AI models.",
  },
  {
    title: "Step 2. Embed the widget",
    detail:
      "You will receive a code snippet that you can embed on your website. This may need a one-time configuration from my side to adjust it to your website. It will work with any website where the transcript is rendered in a consistent way. For synchronizing with the audio player, currently only the Acast player component is supported, but it can be adapted for other player components if needed as long as they provide a way to integrate.",
  },
  {
    title: "Step 3. Your audience enjoys the interactive transcript",
    detail:
      "The code will automatically highlight each word as the audio plays and automatically scrolls to the current position in the transcript, so your listeners never have to search in the text. They can also request translations for any paragraph to aid their comprehension.",
  },
];

export const CreatorsPageContent = () => {
  return (
    <div
      className={css`
        box-sizing: border-box;
        padding-top: 1rem;
      `}
    >
      <HeroSection />
      <FeaturesSection />
      <PricingSection />
    </div>
  );
};

const GoToAppButton = () => {
  return (
    <NextLinkButton href="/login" variant="contained" size="large">
      Get Started
    </NextLinkButton>
  );
};

const HeroSection = () => {
  return (
    <HeroSideBySide
      headline={headline}
      subheadlines={subheadlines}
      afterLogo={<AudienceTabs activeIndex={1} />}
      callToActionContent={<GoToAppButton />}
    />
  );
};

const FeaturesSection = () => {
  return (
    <Section>
      {features.map(({ title, detail }, index) => (
        <Box key={index} sx={{ my: 8 }} component="div">
          <Typography variant="h4" sx={{ mb: 1 }} component="h5">
            {title}
          </Typography>
          <Typography component="p">{detail}</Typography>
        </Box>
      ))}
    </Section>
  );
};

const PricingSection = () => {
  return (
    <Section>
      <Typography variant="h4" sx={{ mb: 1 }} component="h4">
        Pricing
      </Typography>

      <PricingContent />
    </Section>
  );
};

const Section = ({ children }: { children: React.ReactNode }) => {
  return (
    <Box
      sx={{
        my: 2,
        textAlign: "left",
        maxWidth: "40em",
        mx: "auto",
        // py: 2,
        px: 3,
      }}
    >
      {children}
    </Box>
  );
};
